/* =====================================================================
   Sheodra Wishlist — the /pages/wishlist page
   ---------------------------------------------------------------------
   Renders the saved list. Reads it from the storage module, NOT from
   localStorage directly, so a future backend swap changes nothing here.

   Product data (price, image, stock) is fetched FRESH from /products/<handle>.js
   on every visit. We deliberately never store price or image in the wishlist:
   jewellery prices move, and a stale price is worse than no list at all.

   Two modes:
     - own list   : /pages/wishlist            -> editable (remove, add to cart)
     - shared list: /pages/wishlist?w=a:1,b    -> read-only + "save to my list"
   ===================================================================== */
(function (w, d) {
  'use strict';

  var root = d.getElementById('wlPage');
  if (!root) return;

  var CURRENCY = root.getAttribute('data-currency') || 'EUR';
  var listEl = d.getElementById('wlList');
  var emptyEl = d.getElementById('wlEmpty');
  var loadingEl = d.getElementById('wlLoading');
  var countEl = d.getElementById('wlCount');
  var actionsEl = d.getElementById('wlActions');
  var sharedBar = d.getElementById('wlSharedBar');

  /* Same rounding/format rules as ijc-product.js and the money-clean snippet: whole units,
     no decimals, thousands separator kept. The shop hands us its own format via
     snippets/money-js.liquid; Intl is only the fallback. */
  function formatMoney(cents) {
    if (w.SheodraMoney) return w.SheodraMoney.format(cents);
    try {
      var needsCode = CURRENCY === 'CAD' || CURRENCY === 'AUD';
      var out = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: needsCode ? 'USD' : CURRENCY,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        useGrouping: false
      }).format(Math.round(cents / 100));
      return needsCode ? CURRENCY + ' ' + out : out;
    } catch (e) {
      return CURRENCY + ' ' + Math.round(cents / 100);
    }
  }

  function sized(url, px) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + px;
  }

  function param(name) {
    var m = new RegExp('[?&]' + name + '=([^&]*)').exec(w.location.search);
    return m ? decodeURIComponent(m[1].replace(/\+/g, ' ')) : null;
  }

  var sharedParam = param('w');
  var isShared = !!sharedParam;

  function wl() {
    return w.SheodraWishlist;
  }

  function currentItems() {
    if (isShared) return wl() ? wl().decode(sharedParam) : [];
    return wl() ? wl().all() : [];
  }

  /* ---- data ------------------------------------------------------- */

  var cache = {};

  function fetchProduct(handle) {
    if (cache[handle]) return Promise.resolve(cache[handle]);
    return fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('not found');
        return r.json();
      })
      .then(function (p) {
        cache[handle] = p;
        return p;
      })
      .catch(function () {
        return null; /* product deleted or unpublished */
      });
  }

  /* Turn a stored {h,v} into everything the row needs, using fresh product data. */
  function resolve(item) {
    return fetchProduct(item.h).then(function (p) {
      if (!p) return null;
      var variant = null;
      if (item.v) {
        for (var i = 0; i < p.variants.length; i++) {
          if (String(p.variants[i].id) === String(item.v)) {
            variant = p.variants[i];
            break;
          }
        }
        /* Variant was removed from the shop — treat it as a product-only save
           instead of dropping the item, so the customer does not silently lose it. */
      }
      var img =
        (variant && variant.featured_image && variant.featured_image.src) || p.featured_image || '';
      return {
        key: item.h + ':' + (item.v == null ? '' : item.v),
        handle: item.h,
        variantId: variant ? variant.id : null,
        title: p.title,
        variantTitle: variant && variant.title !== 'Default Title' ? variant.title : '',
        price: variant ? variant.price : p.price,
        available: variant ? variant.available : p.available,
        image: img,
        url: p.url + (variant ? '?variant=' + variant.id : '')
      };
    });
  }

  /* ---- rendering (DOM only — no innerHTML with remote data) --------- */

  function el(tag, cls, text) {
    var e = d.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }

  function row(it) {
    var li = el('li', 'wl-row');
    li.setAttribute('data-key', it.key);

    var a = el('a', 'wl-row__media');
    a.href = it.url;
    if (it.image) {
      var img = el('img', 'wl-row__img');
      img.src = sized(it.image, 200);
      img.alt = it.title;
      img.width = 96;
      img.height = 96;
      img.loading = 'lazy';
      a.appendChild(img);
    }
    li.appendChild(a);

    var body = el('div', 'wl-row__body');
    var t = el('a', 'wl-row__title', it.title);
    t.href = it.url;
    body.appendChild(t);
    if (it.variantTitle) body.appendChild(el('div', 'wl-row__variant', it.variantTitle));
    body.appendChild(el('div', 'wl-row__price', formatMoney(it.price)));
    if (!it.available) body.appendChild(el('div', 'wl-row__soldout', 'Sold out'));
    li.appendChild(body);

    var actions = el('div', 'wl-row__actions');

    if (it.variantId && it.available) {
      var add = el('button', 'wl-btn wl-btn--primary', 'Add to cart');
      add.type = 'button';
      add.setAttribute('data-add', String(it.variantId));
      actions.appendChild(add);
    } else if (!it.variantId) {
      /* Saved from a card, so no metal/size was chosen yet. */
      var choose = el('a', 'wl-btn wl-btn--ghost', 'Choose options');
      choose.href = it.url;
      actions.appendChild(choose);
    }

    if (!isShared) {
      var rm = el('button', 'wl-row__remove', '');
      rm.type = 'button';
      rm.setAttribute('aria-label', 'Remove ' + it.title);
      rm.setAttribute('data-remove', it.key);
      rm.innerHTML =
        '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
      actions.appendChild(rm);
    }

    li.appendChild(actions);
    return li;
  }

  function setCount(n) {
    if (countEl) countEl.textContent = n === 1 ? '1 piece' : n + ' pieces';
    /* keep the reserved height honest as the list shrinks */
    d.documentElement.style.setProperty('--wl-rows', String(n));
  }

  /* Every render fetches products, so it finishes asynchronously. If the customer
     removes two items quickly, an OLDER render can resolve after a newer one and
     repaint rows that are already gone. Each render takes a ticket; a render that
     is no longer the latest throws its result away. */
  var renderToken = 0;

  function render() {
    var token = ++renderToken;
    var items = currentItems();

    if (!items.length) {
      if (loadingEl) loadingEl.hidden = true;
      listEl.textContent = '';
      listEl.hidden = true;
      if (emptyEl) emptyEl.hidden = false;
      if (actionsEl) actionsEl.hidden = true;
      paintNotify(false);
      setCount(0);
      return;
    }

    Promise.all(items.map(resolve)).then(function (resolved) {
      if (token !== renderToken) return; /* a newer render already won */
      var live = resolved.filter(Boolean);

      /* Products that no longer exist: drop them from the saved list quietly. */
      if (!isShared && live.length !== items.length && wl()) {
        var alive = {};
        live.forEach(function (r) {
          alive[r.key] = true;
        });
        items.forEach(function (it) {
          var k = it.h + ':' + (it.v == null ? '' : it.v);
          if (!alive[k]) wl().remove(k);
        });
      }

      if (loadingEl) loadingEl.hidden = true;

      if (!live.length) {
        listEl.hidden = true;
        if (emptyEl) emptyEl.hidden = false;
        if (actionsEl) actionsEl.hidden = true;
        paintNotify(false);
        setCount(0);
        return;
      }

      lastLive = live; /* feeds the jewellery-type tags on the opt-in form */
      listEl.textContent = '';
      live.forEach(function (it) {
        listEl.appendChild(row(it));
      });
      listEl.hidden = false;
      if (emptyEl) emptyEl.hidden = true;
      if (actionsEl) actionsEl.hidden = false;
      paintNotify(true);
      setCount(live.length);
    });
  }

  /* ---- actions ----------------------------------------------------- */

  listEl.addEventListener('click', function (e) {
    var t = e.target.closest ? e.target.closest('[data-remove],[data-add]') : null;
    if (!t) return;

    var rmKey = t.getAttribute('data-remove');
    if (rmKey) {
      e.preventDefault();
      if (wl()) wl().remove(rmKey); /* wishlist:change re-renders */
      return;
    }

    var vid = t.getAttribute('data-add');
    if (vid) {
      e.preventDefault();
      t.disabled = true;
      t.textContent = 'Adding...';
      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(vid), quantity: 1 })
      })
        .then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return r.json();
        })
        .then(function () {
          t.textContent = 'Added ✓';
          t.classList.add('is-added');
          bumpCartBadge();
        })
        .catch(function () {
          t.disabled = false;
          t.textContent = 'Add to cart';
        });
    }
  });

  /* The header cart badge is rendered by Liquid, so it would only update on reload.
     Nudge it here so the page stays truthful without a jarring refresh. */
  function bumpCartBadge() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (c) {
        var badges = d.querySelectorAll('.ijc-header__cart-count, .ijc-mobile-menu__cart-count');
        for (var i = 0; i < badges.length; i++) badges[i].textContent = c.item_count;
      })
      .catch(function () {});
  }

  /* Copy link / email the list — this is what makes the list travel to another
     device (and to the person buying the gift) without any server. */
  var copyBtn = d.getElementById('wlCopy');
  if (copyBtn) {
    copyBtn.addEventListener('click', function () {
      var url = isShared ? w.location.href : wl().shareUrl();
      var done = function () {
        var old = copyBtn.textContent;
        copyBtn.textContent = 'Link copied ✓';
        setTimeout(function () {
          copyBtn.textContent = old;
        }, 2000);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(done, done);
      } else {
        w.prompt('Copy your wishlist link:', url);
      }
    });
  }

  var mailBtn = d.getElementById('wlEmail');
  if (mailBtn) {
    mailBtn.addEventListener('click', function () {
      var url = isShared ? w.location.href : wl().shareUrl();
      w.location.href =
        'mailto:?subject=' +
        encodeURIComponent('My Sheodra wishlist') +
        '&body=' +
        encodeURIComponent('Here is my wishlist:\n\n' + url);
    });
  }

  /* Shared list -> copy it into the visitor's own list. */
  var saveMine = d.getElementById('wlSaveMine');
  if (saveMine) {
    saveMine.addEventListener('click', function () {
      var items = wl().decode(sharedParam);
      items.forEach(function (it) {
        wl().add(it);
      });
      w.location.href = '/pages/wishlist';
    });
  }

  /* ---- email opt-in ------------------------------------------------
     A plain Shopify customer form. We intercept it only to avoid a page reload;
     without JS it still submits natively, so the lead is never lost.
     It captures a LEAD — it does not promise to email the list, because we cannot. */

  var notifyEl = d.getElementById('wlNotify');
  var notifyOk = d.getElementById('wlNotifyOk');
  var notifyForm = d.getElementById('wlNotifyForm');

  /* What the visitor actually has in the list right now — used to tag the lead by the
     kind of jewellery they saved, so campaigns can be "you like rings" instead of a
     generic blast. */
  var lastLive = [];

  /* Order matters: "earring" CONTAINS "ring". Check earrings first or every pair of
     earrings gets tagged as a ring. Same rule the cross-sell uses. */
  function categoryOf(title) {
    var t = (title || '').toLowerCase();
    if (t.indexOf('earring') > -1) return 'earring';
    if (t.indexOf('necklace') > -1 || t.indexOf('pendant') > -1) return 'necklace';
    if (t.indexOf('bracelet') > -1 || t.indexOf('bangle') > -1) return 'bracelet';
    if (t.indexOf('ring') > -1 || t.indexOf('band') > -1) return 'ring';
    return null;
  }

  function buildTags() {
    var tags = ['wishlist-lead', 'newsletter'];
    var seen = {};
    for (var i = 0; i < lastLive.length; i++) {
      var c = categoryOf(lastLive[i].title);
      if (c && !seen[c]) {
        seen[c] = true;
      }
    }
    var cats = Object.keys(seen).slice(0, 4); /* cap: never bloat the customer record */
    for (var j = 0; j < cats.length; j++) tags.push('wl-' + cats[j]);
    return tags.join(', ');
  }

  function showNotifyOk() {
    if (notifyEl) notifyEl.hidden = true;
    if (notifyOk) notifyOk.hidden = false;
  }

  /* No-JS path: Shopify redirects back with ?customer_posted=true after a successful
     signup, so honour that on load. */
  if (/[?&]customer_posted=true/.test(w.location.search)) {
    try {
      sessionStorage.setItem('wl_notified', '1');
    } catch (e) {}
  }
  var alreadyNotified = false;
  try {
    alreadyNotified = sessionStorage.getItem('wl_notified') === '1';
  } catch (e) {}

  if (notifyForm) {
    notifyForm.addEventListener('submit', function (e) {
      var btn = notifyForm.querySelector('button[type=submit]');
      /* Stamp the jewellery-type tags right before we read the form, so they reflect
         what is in the list at this exact moment. Without JS the hidden input keeps its
         static default, so the lead is still captured — just without the type tags. */
      var tagsInput = notifyForm.querySelector('input[name="contact[tags]"]');
      if (tagsInput) tagsInput.value = buildTags();
      var data = new FormData(notifyForm);
      /* If FormData/fetch are unavailable, let the browser submit it the old way. */
      if (!w.fetch || !w.FormData) return;
      e.preventDefault();
      if (btn) {
        btn.disabled = true;
        btn.textContent = 'Sending...';
      }
      fetch(notifyForm.action, {
        method: 'POST',
        body: data,
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      })
        .then(function () {
          try {
            sessionStorage.setItem('wl_notified', '1');
          } catch (err) {}
          alreadyNotified = true;
          showNotifyOk();
        })
        .catch(function () {
          /* Network hiccup: fall back to a real submit rather than swallowing the lead. */
          notifyForm.submit();
        });
    });
  }

  /* The opt-in only makes sense next to a list that actually has something in it. */
  function paintNotify(hasItems) {
    if (isShared) {
      if (notifyEl) notifyEl.hidden = true;
      if (notifyOk) notifyOk.hidden = true;
      return;
    }
    if (alreadyNotified) {
      if (notifyEl) notifyEl.hidden = true;
      if (notifyOk) notifyOk.hidden = !hasItems;
      return;
    }
    if (notifyEl) notifyEl.hidden = !hasItems;
    if (notifyOk) notifyOk.hidden = true;
  }

  /* ---- boot -------------------------------------------------------- */

  if (isShared) {
    if (sharedBar) sharedBar.hidden = false;
    root.classList.add('wl-page--shared');
  }

  w.addEventListener('wishlist:change', function () {
    if (!isShared) render();
  });

  render();
})(window, document);
